import type { AppLocale } from "@/i18n/routing";

export type Messages = Record<string, unknown>;
export type ClerkLocalization = typeof import("@clerk/localizations").enUS;

export type LocaleResources = {
  locale: AppLocale;
  messages: Messages;
  clerkLocalization: ClerkLocalization;
};

const localeResourceCache = new Map<AppLocale, Promise<LocaleResources>>();

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function deepMerge(base: Messages, override: Messages): Messages {
  const merged: Messages = { ...base };

  for (const [key, value] of Object.entries(override)) {
    const existing = merged[key];

    if (isRecord(existing) && isRecord(value)) {
      merged[key] = deepMerge(existing, value);
      continue;
    }

    merged[key] = value;
  }

  return merged;
}

// One lazy loader per supported locale. Typed as Record<AppLocale, …> so adding
// a locale to `routing.locales` forces a matching entry here at compile time.
const localeResourceLoaders: Record<AppLocale, () => Promise<LocaleResources>> = {
  en: () => import("@/i18n/resources/en").then((module) => module.enLocaleResources),
  fr: () => import("@/i18n/resources/fr").then((module) => module.frLocaleResources),
  rw: () => import("@/i18n/resources/rw").then((module) => module.rwLocaleResources),
};

export function loadLocaleResources(locale: AppLocale): Promise<LocaleResources> {
  const cached = localeResourceCache.get(locale);
  if (cached) {
    return cached;
  }

  const loader = localeResourceLoaders[locale] ?? localeResourceLoaders.fr;
  const promise = loader();

  localeResourceCache.set(locale, promise);
  return promise;
}
